import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Bell, XLogo } from '@phosphor-icons/react';
import Skeleton from 'react-loading-skeleton';
import { Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { getEtherscanURLForTransactionHash } from '@manifoldxyz/js-ts-utils/urls';
import { MediaDisplay } from '@manifoldxyz/react-component-library';
import animationJson from '@/assets/animation.json?url';
import { TELEMETRY_EVENT_MAP, trackedGalleryTokenLink } from '@/common/telemetry';
import { generateCreateYourOwnLink } from '@/common/util';
import StyledButton from '@/components/StyledButton';
import { useCreatorEnsOrFormattedWalletAddress, useTwitterHandle } from '@/hooks/identity';
import { useInstanceLink } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import useMainStore from '@/store/mainStore';

interface SuccessComponentProps {
  awaitingConfirmation: boolean;
  totalMinted?: number;
}

export default function SuccessComponent({
  awaitingConfirmation,
  totalMinted,
}: SuccessComponentProps) {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const mintedTxHash = useMainStore((state) => state.mintedTxHash);
  const mintToAddress = useMainStore((state) => state.mintToAddress);
  const twitterHandle = useTwitterHandle();
  const creatorEnsOrFormattedWalletAddress = useCreatorEnsOrFormattedWalletAddress();
  const mintedTokenIds = useMainStore((state) => state.mintedTokenIds);
  const analytics = useAnalytics();
  const instanceLink = useInstanceLink();

  const assetName = studioInstance?.publicData.asset.name;

  // Check if the NFT was gifted
  const isGiftedNFT = !!mintToAddress;
  const formattedMintToAddress = mintToAddress ? StringUtils.abbreviateAddress(mintToAddress) : '';

  const viewTransactionLink = getEtherscanURLForTransactionHash(
    studioInstance?.publicData.network ?? Network.NetworkId.Mainnet,
    mintedTxHash ?? '',
  );

  let tweetText = '';
  if (twitterHandle) {
    tweetText = `I just minted ${assetName} from ${twitterHandle} via @manifoldxyz%0A%0A${instanceLink}?utm_source=minted_tweet`;
  } else {
    tweetText = `I just minted ${assetName} from ${creatorEnsOrFormattedWalletAddress} via @manifoldxyz%0A%0A${instanceLink}?utm_source=minted_tweet`;
  }

  if (awaitingConfirmation || !studioInstance) {
    return (
      <div className="m-claim--flex m-claim--size-full m-claim--flex-col m-claim--gap-4 m-claim--overflow-y-auto m-claim--rounded-lg m-claim--bg-surface-secondary m-claim--p-6">
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-4">
          <Skeleton height="150px" />
          <Skeleton height="46px" />
          <Skeleton height="200px" />
        </div>
      </div>
    );
  }

  const openSubscribeModal = () => {
    window.dispatchEvent(new CustomEvent('m-prompt-subscribe')); // TODO: this currently doesnt open the modal, put it here so we customize the subscribe button, waiting on this branch: https://github.com/manifoldxyz/subscribe-widget/tree/jaxon/style/2143-icon
  };

  return (
    <div className="m-claim--relative m-claim--flex m-claim--w-full m-claim--flex-col m-claim--overflow-y-auto m-claim--rounded-lg m-claim--bg-surface-secondary">
      <DotLottieReact
        src={animationJson}
        loop
        autoplay
        speed={1}
        layout={{
          fit: 'cover',
          align: [0.5, 0.5],
        }}
        className="m-claim--absolute m-claim--inset-0 m-claim--size-full"
      />
      <div className="m-claim--relative m-claim--m-auto m-claim--my-8 m-claim--size-[260px] md:m-claim--size-[320px]">
        <div className="m-claim--flex m-claim--size-[260px] m-claim--items-center m-claim--justify-center md:m-claim--size-[320px]">
          <MediaDisplay imageUrl={studioInstance?.publicData.asset.image} />
        </div>
      </div>

      <div className="m-claim--flex m-claim--flex-col m-claim--gap-3 m-claim--px-6">
        <div className="m-claim--flex m-claim--flex-col m-claim--gap-8">
          <div className="m-claim--flex m-claim--flex-col m-claim--items-center m-claim--gap-1 m-claim--pb-0">
            <div className="m-claim--px-6 m-claim--text-center m-claim--text-md m-claim--font-normal m-claim--text-primary">
              {isGiftedNFT ? (
                <span className="m-claim--font-sans m-claim--text-secondary">You gifted</span>
              ) : (
                <span className="m-claim--font-sans m-claim--text-secondary">You now own</span>
              )}{' '}
              <>
                {!!totalMinted && totalMinted > 1 && (
                  <>
                    {totalMinted}{' '}
                    <span className="m-claim--font-sans m-claim--text-secondary">of</span>{' '}
                  </>
                )}
                {assetName} <span className="m-claim--font-sans m-claim--text-secondary">by</span>{' '}
                {studioInstance?.creator.name ?? creatorEnsOrFormattedWalletAddress}
                {isGiftedNFT && (
                  <>
                    {' '}
                    <span className="m-claim--font-sans m-claim--text-secondary">to</span>{' '}
                    {formattedMintToAddress}
                  </>
                )}
              </>
            </div>
            <div className="m-claim--flex m-claim--flex-row m-claim--items-center m-claim--justify-center m-claim--gap-1">
              {true && (
                <a
                  href={viewTransactionLink}
                  target="_blank"
                  className="m-claim--mt-2 m-claim--flex m-claim--cursor-pointer m-claim--flex-row m-claim--items-center m-claim--text-nowrap m-claim--text-sm m-claim--text-muted m-claim--underline"
                >
                  <span className="m-claim--text-sm m-claim--font-normal m-claim--text-muted m-claim--underline hover:m-claim--text-primary">
                    View transaction
                  </span>
                </a>
              )}
              <span className="m-claim--mt-2 m-claim--flex m-claim--cursor-pointer m-claim--flex-row m-claim--items-center m-claim--text-nowrap m-claim--text-sm m-claim--text-muted">
                {' '}
                |{' '}
              </span>
              {mintedTokenIds.length > 0 && (
                <a
                  href={trackedGalleryTokenLink(
                    studioInstance?.publicData.network ?? Network.NetworkId.Mainnet,
                    studioInstance?.publicData.contract.contractAddress ?? '',
                    mintedTokenIds[mintedTokenIds.length - 1],
                    {
                      medium: 'mint_success',
                      campaign: 'mint_success',
                    },
                  )}
                  target="_blank"
                  className="m-claim--mt-2 m-claim--flex m-claim--cursor-pointer m-claim--flex-row m-claim--items-center m-claim--text-nowrap m-claim--text-sm m-claim--text-muted m-claim--underline"
                >
                  <span className="m-claim--text-sm m-claim--font-normal m-claim--text-muted m-claim--underline hover:m-claim--text-primary">
                    View my token
                  </span>
                </a>
              )}
            </div>
          </div>
          <div className="m-claim--flex m-claim--flex-col m-claim--items-center m-claim--justify-center m-claim--gap-3">
            <StyledButton
              version="primary"
              className="m-claim--flex m-claim--h-[50px] m-claim--w-1/2 m-claim--flex-row m-claim--items-center m-claim--justify-center m-claim--gap-2 m-claim--uppercase"
              onClick={() => {
                window.open(`https://x.com/intent/tweet?text=${tweetText}`, '_blank');
              }}
            >
              <XLogo
                size={24}
                color="#0a0a0a"
                weight="fill"
              />
              <span className="m-claim--mt-1 m-claim--font-semimono m-claim--font-semibold">
                Share on X
              </span>
            </StyledButton>
            <StyledButton
              version="secondary"
              className="m-claim--flex m-claim--h-[50px] m-claim--w-1/2 m-claim--flex-row m-claim--items-center m-claim--justify-center m-claim--gap-2 m-claim--uppercase"
              onClick={openSubscribeModal}
            >
              <Bell
                size={24}
                color="#ebe5e5"
                weight="fill"
              />
              <span className="m-claim--font-semimono m-claim--font-semibold">Subscribe</span>
            </StyledButton>
          </div>
          <button
            onClick={() => {
              analytics.capture({
                event: TELEMETRY_EVENT_MAP.CREATE_YOUR_OWN_BUTTON_CLICK,
              });
              window.open(generateCreateYourOwnLink(studioInstance), '_blank');
            }}
            className="m-claim--m-auto m-claim--text-sm m-claim--font-normal m-claim--text-muted m-claim--underline hover:m-claim--text-primary"
          >
            Create your own
          </button>
        </div>
      </div>
    </div>
  );
}
